import {
  WebMercatorViewport,
  type MapViewState,
  type PickingInfo,
} from '@deck.gl/core';
import { getCentroid, getGeometricCenter } from './utils';
import { METADATA_SESSION_KEY, SMALL_SCREEN_WIDTH } from '@/app/constants';
import type { WebMercatorViewportOptions, Padding } from './types';
import type { PopupOptions } from 'react-map-gl/mapbox';
import type { SelectedItem } from '@/features/analysisMapSlice';
import { pointToTile, tileToQuadkey } from '@mapbox/tilebelt';
import type {
  Feature,
  Geometry,
  Point,
  Position,
  LineString,
  Polygon,
  MultiLineString,
} from 'geojson';
import { captureException } from '@sentry/core';

type SelectedFeature = NonNullable<SelectedItem['object']>;

// Offset to take into account the Analysis List component
const LIST_OFFSET_PX = 355;

// Offset to take into account the Map layer manager component
export const LAYER_MANAGER_OFFSET_PX = 320;

// Offset to take into account search bar in Mobile view
const SEARCH_BAR_OFFSET_PX = 90;

export const DEFAULT_BUFFER_PX = 20;
export const DESKTOP_VIEWBOX_PADDING = {
  top: DEFAULT_BUFFER_PX,
  bottom: DEFAULT_BUFFER_PX,
  left: LIST_OFFSET_PX + DEFAULT_BUFFER_PX,
  right: DEFAULT_BUFFER_PX,
};
export const MOBILE_VIEWBOX_PADDING = {
  ...DESKTOP_VIEWBOX_PADDING,
  left: DEFAULT_BUFFER_PX,
  bottom: DEFAULT_BUFFER_PX + SEARCH_BAR_OFFSET_PX,
};
export const DEFAULT_FALLBACK_ZOOM_LEVEL = 16;

export const getViewportFromBbox = (
  viewState: WebMercatorViewportOptions,
  bbox: number[],
  padding: number | Required<Padding>
) => {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  const viewport = new WebMercatorViewport(viewState);
  try {
    return viewport.fitBounds(
      [
        [minLon, minLat],
        [maxLon, maxLat],
      ],
      { padding }
    );
  } catch (error) {
    captureException(error);
    const [longitude, latitude] = getCentroid([
      [minLon, minLat],
      [maxLon, maxLat],
    ]);

    return {
      longitude,
      latitude,
      zoom: DEFAULT_FALLBACK_ZOOM_LEVEL,
    };
  }
};

export type ViewState = MapViewState & {
  bearing: number;
  pitch: number;
  bbox?: [number, number, number, number];
};

/**
 * Reads `zoom`, `lat`, and `lng` from URL search params. Returns null if any
 * are missing, non-finite, or out of range.
 */
export const parseUrlViewState = (
  params: URLSearchParams
): MapViewState | null => {
  const [zoom, lat, lng] = ['zoom', 'lat', 'lng'].map((k) => {
    const raw = params.get(k)?.trim();
    return raw ? Number(raw) : NaN;
  });
  if (![zoom, lat, lng].every(Number.isFinite)) return null;
  if (zoom < 0 || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { latitude: lat, longitude: lng, zoom, bearing: 0, pitch: 0 };
};

export const getInitialViewState: () => ViewState = () => {
  const base = { bearing: 0, pitch: 0 };

  const fromUrl = parseUrlViewState(
    new URLSearchParams(window.location.search)
  );
  if (fromUrl) {
    return {
      ...base,
      longitude: fromUrl.longitude,
      latitude: fromUrl.latitude,
      zoom: fromUrl.zoom,
    };
  }

  const storedState = sessionStorage.getItem(METADATA_SESSION_KEY);
  if (storedState) {
    const parsedMetadata = JSON.parse(storedState);

    // This comes from what's saved in the database
    if (parsedMetadata.map_config) {
      return {
        ...base,
        ...parsedMetadata.map_config,
      };
    }
  }

  // If we don't have any saved state, we should use the default coordinates
  return {
    ...base,
    longitude: -75.46342,
    latitude: 38.81147,
    zoom: 10,
  };
};

export const toSelectedItem = ({
  object,
  layer,
  coordinate,
}: PickingInfo<SelectedFeature>): SelectedItem => ({
  object,
  layer: layer ? { id: layer.id } : undefined,
  coordinate,
});

export const getGeometryPositions = (geometry: Geometry): Position[] => {
  switch (geometry.type) {
    case 'Point':
      return [geometry.coordinates];
    case 'MultiPoint':
    case 'LineString':
      return geometry.coordinates;
    case 'MultiLineString':
    case 'Polygon':
      return geometry.coordinates.flat();
    case 'MultiPolygon':
      return geometry.coordinates.flat(2);
    case 'GeometryCollection':
      return geometry.geometries.flatMap(getGeometryPositions);
  }
};

const projectUnprojectCoordinates = (
  selectedFeature: SelectedFeature,
  viewState: WebMercatorViewportOptions
) => {
  const viewport = new WebMercatorViewport(viewState);
  const [minLon, minLat, maxLon, maxLat]: number[] = selectedFeature.bbox ?? [];

  //Projects world coordinates to pixel coordinates on screen.
  const projectCoordinates = {
    leftBottom: viewport.project([minLon, minLat]),
    leftTop: viewport.project([minLon, maxLat]),
    rightTop: viewport.project([maxLon, maxLat]),
    rightBottom: viewport.project([maxLon, minLat]),
  };

  //Unproject pixel coordinates on screen into world coordinates.
  const unprojectCoordinates = {
    top: viewport.unproject(
      getGeometricCenter([
        projectCoordinates.leftTop,
        projectCoordinates.rightTop,
      ])
    ),
    right: viewport.unproject(
      getGeometricCenter([
        projectCoordinates.rightTop,
        projectCoordinates.rightBottom,
      ])
    ),
  };

  return {
    project: projectCoordinates,
    unproject: unprojectCoordinates,
  };
};

export interface BoundingBoxCoordinatesReturn {
  coordinates: number[];
  anchor: PopupOptions['anchor'];
  popupOffset: number;
}

export const getBoundingBoxCoordinates = (
  viewState: WebMercatorViewportOptions,
  selectedFeature: SelectedFeature,
  popupHeight: number,
  isLayerManagerOpen = false
): BoundingBoxCoordinatesReturn => {
  const DEFAULT_POPUP_WIDTH = 400;
  const POPUP_OFFSET_SMALL = 20;
  const POPUP_OFFSET_LARGE = 100;
  const { project, unproject } = projectUnprojectCoordinates(
    selectedFeature,
    viewState
  );
  const { width = 0 } = viewState;

  const rightOffset = isLayerManagerOpen ? LAYER_MANAGER_OFFSET_PX : 0;

  let popupOffset = 0;
  let coordinates = [0, 0];
  let anchor: PopupOptions['anchor'] = 'left';

  if (project.rightTop[1] >= popupHeight + POPUP_OFFSET_SMALL) {
    coordinates = unproject.top;
    anchor = 'bottom';
    popupOffset = POPUP_OFFSET_SMALL;
  } else if (
    width - rightOffset - project.rightTop[0] >=
      DEFAULT_POPUP_WIDTH + POPUP_OFFSET_SMALL &&
    width - project.rightBottom[0] >= DEFAULT_POPUP_WIDTH + POPUP_OFFSET_SMALL
  ) {
    coordinates = unproject.right;
    anchor = 'left';
    popupOffset = POPUP_OFFSET_SMALL;
  } else {
    const { geometry } = selectedFeature;
    if (geometry.type === 'Point') {
      coordinates = geometry.coordinates;
    } else {
      coordinates = getCentroid(getGeometryPositions(geometry)); //Using this calculation incase there is no space on top or right
    }
    anchor = 'bottom-left';
    popupOffset = POPUP_OFFSET_LARGE;
  }

  return {
    coordinates,
    anchor,
    popupOffset,
  };
};

export const updateViewport = (
  v: WebMercatorViewportOptions,
  data: number[] | null | undefined,
  width: number,
  DESKTOP_VIEWBOX_PADDING: Required<Padding>,
  MOBILE_VIEWBOX_PADDING: Required<Padding>
) => {
  // Skip updating if width and height are unavailable
  if (!v.height && !v.width) {
    return v;
  }

  if (data) {
    // Dynamically choose padding based on screen width
    const viewport = getViewportFromBbox(
      v,
      data,
      width >= SMALL_SCREEN_WIDTH
        ? DESKTOP_VIEWBOX_PADDING
        : MOBILE_VIEWBOX_PADDING
    );

    // If a valid viewport is returned, merge it with the existing view state
    if (viewport) {
      return {
        ...v,
        ...viewport,
      };
    }
  }

  // Return the original view state if no changes are needed
  return v;
};

export function coordsToQuadkey(lat: number, lng: number): string {
  // NOTE: we hard code this to 14, because that's the typical quadkey
  // in our mosaic jsons, so we directly get a quadkey we can look up
  return tileToQuadkey(pointToTile(lng, lat, 14));
}

/**
 * Helper to validate coordinate arrays (2D or 3D).
 * Accepts exactly 2 or 3 numeric elements [lon, lat] or [lon, lat, elevation].
 */
export function isCoordinates(
  value: unknown
): value is [number, number] | [number, number, number] {
  return (
    Array.isArray(value) &&
    (value.length === 2 || value.length === 3) &&
    value.every((coord) => typeof coord === 'number')
  );
}

/**
 * Extract [longitude, latitude] coordinates from a deck.gl picked object.
 * Handles both regular deck.gl objects and GeoJSON features with various geometry types.
 */
export function extractCoordinates(
  object:
    | { position: number[] }
    | Feature<Point | LineString | Polygon | MultiLineString>
): [number, number] | null {
  // Regular deck.gl object with position property (i.e. a hazard tree)
  if ('position' in object && isCoordinates(object.position)) {
    return [object.position[0], object.position[1]];
  }

  // GeoJSON Features
  if (!('geometry' in object)) {
    return null;
  }

  const geometry = object.geometry;
  if (!geometry || !Array.isArray(geometry.coordinates)) {
    return null;
  }

  const coords = geometry.coordinates;

  // Point: coordinates = [lon, lat] or [lon, lat, elevation]
  if (geometry.type === 'Point' && isCoordinates(coords)) {
    return [coords[0], coords[1]];
  }

  // LineString: coordinates = [[lon, lat], ...]
  if (
    geometry.type === 'LineString' &&
    Array.isArray(coords[0]) &&
    isCoordinates(coords[0])
  ) {
    return [coords[0][0], coords[0][1]];
  }

  // MultiLineString or Polygon: coordinates = [[[lon, lat], ...], ...]
  if (
    (geometry.type === 'MultiLineString' || geometry.type === 'Polygon') &&
    Array.isArray(coords[0]) &&
    Array.isArray(coords[0][0]) &&
    isCoordinates(coords[0][0])
  ) {
    return [coords[0][0][0], coords[0][0][1]];
  }

  return null;
}
